import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { detectUserLanguage, loadTranslations, SUPPORTED_LANGUAGES } from '../utils/languageDetection';
import debug from '../utils/debug';

// Initialize i18n with English translations already loaded
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false // React already escapes values
    },
    detection: {
      // Prioritize navigator (browser language) over localStorage
      order: ['navigator', 'localStorage'],
      caches: ['localStorage']
    },
    resources: {
      en: {
        translation: {
          "mainHeading": "Toby Brooks | Polymath | Servant Leader",
          "tagline": "Empowering dreams. Fostering connections. Transforming challenges into opportunities.",
          "description": "A new digital space where innovation meets inspiration - designed for those seeking to learn, grow, connect, and live meaningfully on their own terms.",
          "callToAction": "Join the journey to unlock potential across your mind, body, and soul.",
          "launchAnnouncement": "Get started now!",
          "formElements": {
            "emailPlaceholder": "Enter your email",
            "submitButton": "Subscribe for Updates",
            "submitting": "Submitting...",
            "successMessage": "Thank you for subscribing!",
            "generalError": "Something went wrong. Please try again.",
            "invalidEmailError": "Please enter a valid email address.",
            "emailExistsError": "This email is already subscribed."
          },
          "footer": {
            "copyright": "© 2025 Toby Brooks All rights reserved.",
            "privacyPolicy": "Privacy Policy",
            "termsConditions": "Terms & Conditions",
            "disclaimer": "Disclaimer",
            "allRightsReserved": "All rights reserved."
          },
          "dualCTAs": {
            "left": {
              "title": "Share Your Requests",
              "subtitle": "Visit our Ideas Portal to submit ideas, vote on polls, and shape content direction",
              "buttonText": "Open Ideas Portal",
              "ariaLabel": "Open Ideas Portal in new window"
            },
            "right": {
              "title": "Explore Insights",
              "subtitle": "Dive deep into Toby's thoughts and discoveries",
              "buttonText": "Visit Blog",
              "ariaLabel": "Visit Toby's blog in new window"
            }
          },
          "admin": {
            "login": {
              "title": "Admin Login",
              "email": "Email",
              "password": "Password",
              "loginButton": "Login",
              "forgotPassword": "Forgot Password?",
              "resetPassword": "Reset Password",
              "backToLogin": "Back to Login"
            },
            "signup": {
              "title": "Admin Signup",
              "email": "Email",
              "password": "Password",
              "confirmPassword": "Confirm Password",
              "signupButton": "Sign Up",
              "passwordRequirements": "Password must be at least 8 characters with uppercase, lowercase, number, and special character."
            },
            "dashboard": {
              "title": "Admin Dashboard",
              "overview": "Overview",
              "siteConfig": "Site Configuration",
              "subscribers": "Subscribers",
              "communications": "Communications",
              "logout": "Logout"
            }
          }
        },
        "backToHome": "Back to Home",
        "privacyPolicy": {
          "lastUpdated": "Last updated: January 2025",
          "informationCollection": {
            "title": "1. Information We Collect",
            "content": "We collect information you provide directly to us, such as when you subscribe to our updates, contact us, or use our services. This may include your email address, name, and any other information you choose to provide."
          },
          "useOfInformation": {
            "title": "2. How We Use Your Information",
            "content": "We use the information we collect to:",
            "item1": "Provide and improve our services",
            "item2": "Send you updates and communications",
            "item3": "Respond to your inquiries and support requests",
            "item4": "Analyze usage patterns and improve user experience"
          },
          "dataProtection": {
            "title": "3. Data Protection",
            "content": "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Your data is stored securely and we follow industry best practices for data protection."
          },
          "cookies": {
            "title": "4. Cookies and Tracking",
            "content": "We use cookies and similar technologies to enhance your experience, analyze site usage, and personalize content. You can manage your cookie preferences through your browser settings."
          },
          "thirdParty": {
            "title": "5. Third-Party Services",
            "content": "We may use third-party services for analytics, hosting, and other purposes. These services may collect information about your use of our website. Please review their privacy policies for more information."
          },
          "yourRights": {
            "title": "6. Your Rights",
            "content": "You have the right to access, update, or delete your personal information. You may also unsubscribe from our communications at any time. To exercise these rights, please contact us using the information below."
          },
          "contact": {
            "title": "7. Contact Us",
            "content": "If you have any questions about this Privacy Policy, please contact us at: "
          }
        },
        "disclaimer": {
          "lastUpdated": "Last updated: January 2025",
          "generalInformation": {
            "title": "1. General Information",
            "content": "The information contained on this website is for general information purposes only. While we endeavor to keep the information up to date and correct, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the website or the information, products, services, or related graphics contained on the website."
          },
          "noWarranties": {
            "title": "2. No Warranties",
            "content": "This website is provided \"as is\" without any representations or warranties, express or implied. We make no representations or warranties in relation to this website or the information and materials provided on this website."
          },
          "limitationOfLiability": {
            "title": "3. Limitation of Liability",
            "content": "In no event will we be liable for any loss or damage including without limitation, indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss of data or profits arising out of, or in connection with, the use of this website."
          },
          "accuracyOfMaterials": {
            "title": "4. Accuracy of Materials",
            "content": "The materials appearing on this website could include technical, typographical, or photographic errors. We do not warrant that any of the materials on its website are accurate, complete, or current. We may make changes to the materials contained on its website at any time without notice."
          },
          "externalLinks": {
            "title": "5. Links to External Sites",
            "content": "Through this website, you may be able to link to other websites which are not under our control. We have no control over the nature, content, and availability of those sites. The inclusion of any links does not necessarily imply a recommendation or endorse the views expressed within them."
          },
          "personalResponsibility": {
            "title": "6. Personal Responsibility",
            "content": "Every effort is made to keep the website up and running smoothly. However, we take no responsibility for, and will not be liable for, the website being temporarily unavailable due to technical issues beyond our control."
          },
          "professionalAdvice": {
            "title": "7. Not Professional Advice",
            "content": "The content on this website is for informational purposes only and should not be considered professional advice. Always seek the advice of qualified professionals regarding any specific questions you may have."
          },
          "modifications": {
            "title": "8. Modifications",
            "content": "We may revise these terms of use for its website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of use."
          },
          "contact": {
            "title": "9. Contact Information",
            "content": "If you have any questions about this disclaimer, please contact us at: "
          }
        }
      }
    }
  });

// Dynamically load translations for other languages
const initializeTranslations = async () => {
  try {
    // First, check the browser language directly
    const browserLang = navigator.language.split('-')[0];
    debug.log(`Browser language from navigator: ${browserLang}`);
    
    // Then use our detection function which prioritizes browser language
    const detectedLanguage = await detectUserLanguage();
    debug.log('Final detected language:', detectedLanguage);
    
    // IMPORTANT: Load all translations BEFORE changing the language
    // This ensures the translations are available when the language is changed
    
    // Pre-load translations for all supported languages except English (already loaded)
    const loadPromises = [];
    
    for (const langCode of SUPPORTED_LANGUAGES) {
      console.log(`[i18n] Pre-loading translations for ${langCode}`);
      debug.log(`Pre-loading translations for ${langCode}`);

      // Create a promise for each language load
      const loadPromise = loadTranslations(langCode).then(translations => {
        if (translations) {
          console.log(`[i18n] Adding resource bundle for ${langCode}, sample keys:`, Object.keys(translations).slice(0, 5));
          if (langCode === 'zh') {
            console.log(`[i18n] Chinese translations loaded with keys:`, Object.keys(translations));
            console.log(`[i18n] Sample Chinese mainHeading:`, translations.mainHeading);
          }
          debug.log(`Adding resource bundle for ${langCode}`);
          i18n.addResourceBundle(
            langCode,
            'translation',
            translations,
            true,
            true
          );
          console.log(`[i18n] Resource bundle for ${langCode} added successfully`);
          return true;
        } else {
          console.warn(`[i18n] No translations loaded for ${langCode}`);
        }
        return false;
      }).catch(error => {
        console.error(`[i18n] Error loading translations for ${langCode}:`, error);
        return false;
      });

      loadPromises.push(loadPromise);
    }
    
    // Wait for all translations to be loaded
    await Promise.all(loadPromises);
    debug.log('All translations loaded');
    
    // NOW change the language after all translations are loaded
    if (SUPPORTED_LANGUAGES.includes(detectedLanguage)) {
      debug.log(`Setting language to ${detectedLanguage} after loading all translations`);
      await i18n.changeLanguage(detectedLanguage);
      
      // Force a re-render of the app by dispatching a custom event
      window.dispatchEvent(new Event('languageLoaded'));
    }
    
    // Double-check that the language was set correctly
    debug.log(`Current language after initialization: ${i18n.language}`);
  } catch (error) {
    debug.error('Failed to initialize translations:', error);
  }
};

// Add a listener for language changes
i18n.on('languageChanged', async (langCode) => {
  console.log(`[i18n] Language changed to ${langCode}`);
  debug.log(`Language changed to ${langCode}`);

  console.log(`[i18n] Has resource bundle for ${langCode}:`, i18n.hasResourceBundle(langCode, 'translation'));

  // If we don't have translations for this language yet, load them
  if (!i18n.hasResourceBundle(langCode, 'translation') && langCode !== 'en') {
    console.log(`[i18n] Loading missing translations for ${langCode}`);
    debug.log(`Loading missing translations for ${langCode}`);
    const translations = await loadTranslations(langCode);

    if (translations) {
      console.log(`[i18n] Adding resource bundle for ${langCode}, keys:`, Object.keys(translations));
      i18n.addResourceBundle(
        langCode,
        'translation',
        translations,
        true,
        true
      );
      console.log(`[i18n] Resource bundle added. Now has ${langCode}:`, i18n.hasResourceBundle(langCode, 'translation'));
    } else {
      console.warn(`[i18n] Failed to load translations for ${langCode}`);
    }
  }
});

initializeTranslations();

// Expose i18n globally for debugging
if (typeof window !== 'undefined') {
  (window as any).i18n = i18n;
  console.log('[i18n] i18n instance exposed globally as window.i18n for debugging');
}

export default i18n;
